/**
 * RiskFactorAdmin.tsx
 * Admin panel for editing risk_factors weights per jurisdiction.
 */

import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { RefreshCw, Save, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';

// Validation schema for risk factor weights
const weightSchema = z.number().min(0).max(1);

interface RiskFactor {
  id: string;
  jurisdiction: string;
  key: string;
  weight: number;
}

export default function RiskFactorAdmin() {
  const [rows, setRows] = useState<RiskFactor[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('risk_factors')
        .select('*')
        .order('jurisdiction', { ascending: true })
        .order('key', { ascending: true });

      if (error) throw error;
      setRows(data || []);
    } catch (err) {
      console.error('Error loading risk factors:', err);
      toast.error('Failed to load risk factors');
    } finally {
      setLoading(false);
    }
  }

  async function save() {
    setSaving(true);
    try {
      // Validate all weights before saving
      const updates = rows.map((r) => {
        const weight = Number(r.weight);
        const validated = weightSchema.safeParse(weight);

        if (!validated.success) {
          throw new Error(`Invalid weight for ${r.key}: must be between 0 and 1`);
        }

        return { id: r.id, weight: validated.data };
      });

      // Update each factor
      const updatePromises = updates.map((u) =>
        supabase.from('risk_factors').update({ weight: u.weight }).eq('id', u.id)
      );

      const results = await Promise.all(updatePromises);
      const errors = results.filter((r) => r.error);

      if (errors.length > 0) {
        throw new Error('Some updates failed');
      }

      toast.success('Weights updated successfully');
      await load(); // Reload to get fresh data
    } catch (err: any) {
      console.error('Error saving risk factors:', err);
      toast.error(err.message || 'Failed to save weights');
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  // Group factors by jurisdiction
  const grouped = rows.reduce(
    (acc, r) => {
      if (!acc[r.jurisdiction]) {
        acc[r.jurisdiction] = [];
      }
      acc[r.jurisdiction].push(r);
      return acc;
    },
    {} as Record<string, RiskFactor[]>
  );

  const handleWeightChange = (id: string, value: string) => {
    // Allow empty string for typing, validate on save
    const numValue = value === '' ? 0 : parseFloat(value);

    setRows(rows.map((r) => (r.id === id ? { ...r, weight: numValue } : r)));
  };

  if (loading) {
    return <div className="p-8 text-center text-muted-foreground">Loading risk factors...</div>;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div className="flex items-center gap-2">
          <Settings className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg font-semibold">Risk Factor Weights</CardTitle>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading || saving}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Reload
          </Button>
          <Button size="sm" onClick={save} disabled={saving || loading}>
            <Save className="h-4 w-4 mr-1" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {Object.entries(grouped).map(([jur, factors]) => (
          <div key={jur}>
            <h3 className="text-base font-semibold mb-3 text-foreground">{jur} - Jurisdiction</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Factor</TableHead>
                  <TableHead>Weight (0-1)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {factors.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell className="font-medium">{f.key}</TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="0"
                        max="1"
                        step="0.01"
                        value={f.weight}
                        onChange={(e) => handleWeightChange(f.id, e.target.value)}
                        className="w-28"
                        disabled={saving}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="text-xs text-muted-foreground mt-2">
              Total weight: {factors.reduce((sum, f) => sum + Number(f.weight), 0).toFixed(3)}
            </p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
